import React, { FC, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { Button } from 'reactstrap';
import { useProxyParameters } from 'utils/api/ApiHooks';
import ConfirmAPI from 'utils/api/ConfirmAPI';
import { getFriendlyErrorMessageIfErrorOccurred } from 'utils/util/util';
import ConfirmationDialogModal from 'views/Widgets/Modals/ConfirmationDialogModal';
import { PersonRole } from './types';

interface Props {
  value: PersonRole;
  onDelete: (value: PersonRole) => void;
}

const AdminRoleDeleteButton: FC<Props> = ({ value, onDelete }) => {
  const { formatMessage } = useIntl();
  const proxyParameters = useProxyParameters();
  const [isOpen, setIsOpen] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const handleOnClick = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleOnClosed = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleOnDeleteCallback = useCallback(() => {
    ConfirmAPI.sendRequestToConfirm(
      'DELETE',
      `/person-roles/${value.id}`,
      proxyParameters,
      (data, error: unknown, hardErrorMessage: unknown) => {
        if (error || hardErrorMessage) {
          setErrorMessage(
            getFriendlyErrorMessageIfErrorOccurred(error, hardErrorMessage)
          );
          return;
        }
        onDelete(value);
        handleOnClosed();
      }
    );
  }, [handleOnClosed, onDelete, proxyParameters, value]);

  return (
    <>
      <Button className="ms-3 btn btn-white" onClick={handleOnClick}>
        <i className="fe fe-trash-2" />
      </Button>
      <ConfirmationDialogModal
        isOpen={isOpen}
        onClosed={handleOnClosed}
        toggle={handleOnClosed}
        confirmCallback={handleOnDeleteCallback}
        title={formatMessage({
          id: 'app.views.administration.people_administration.administrators.admin_role_delete_button.title.delete_administrator_role',
          defaultMessage: 'Delete administrator role',
        })}
        description={formatMessage({
          id: 'app.views.administration.people_administration.administrators.admin_role_delete_button.description.delete_administrator_role',
          defaultMessage:
            'Are you sure that you want to remove this person from this administrator role?',
        })}
        confirmText={formatMessage({
          id: 'app.views.administration.people_administration.administrators.admin_role_delete_button.confirmText.delete',
          defaultMessage: 'Delete',
        })}
        validationErrors={errorMessage}
      />
    </>
  );
};

export default React.memo(AdminRoleDeleteButton);
